import { LOCALE_SPLIT_TOKEN, WORKFLOW_STEP_TYPES } from '../../../global/constants';
import { Map as ImmutableMap } from 'immutable';
import { addTenantSubworkflow, updateTenantSubworkflow } from './api-calls';
import { nanoid } from 'nanoid';

export const createLocaleSpecifier = workflow => {
  return workflow.sourceLocale + LOCALE_SPLIT_TOKEN + workflow.targetLocale;
};

export const createLocaleSpecifierFromParts = (sourceLocale, targetLocale) => {
  return sourceLocale + LOCALE_SPLIT_TOKEN + targetLocale;
};

export const parseLocaleSpecifier = localeSpecifier => {
  return localeSpecifier.split(LOCALE_SPLIT_TOKEN);
};

export const validateSubworkflow = subWorkflow => {
  const errorList = [];
  if (!subWorkflow.sourceLocale) {
    errorList.push('No source locale specified');
  }
  if (!subWorkflow.targetLocale) {
    errorList.push('No target locale specified');
  }
  if (!subWorkflow.startAt && subWorkflow.startAt !== 0) {
    errorList.push('No starting node specified');
  }
  for (const step of subWorkflow.steps) {
    if (step.type === WORKFLOW_STEP_TYPES.SegmentAndFilter || step.type === WORKFLOW_STEP_TYPES.Merge) {
      continue;
    }

    if (step.type === WORKFLOW_STEP_TYPES.AB) {
      if (!step.parameters.allocations || step.parameters.allocations.length < 2) {
        errorList.push('AB test configured with no treatment groups');
      }
      continue;
    }

    if (step.type === WORKFLOW_STEP_TYPES.Luma) {
      if (!step.parameters || !step.parameters.modelId) {
        errorList.push('LUMA step does not have a model configured');
      }
      continue;
    }

    if (!step.parameters || !step.parameters.roleArn || !step.parameters.resourceId) {
      errorList.push(`${step.type} step does not have a resource selected`);
    }
  }

  return errorList;
};

/**
 * Saves all subworkflows for a workflow; if one subworkflow fails, it will stop saving
 * @param {string} tenantId - tenant ID to save this for
 * @param {object} workflowData - workflow object to save subworkflows from
 * @param {object} workflowGraphs - workflow graphs corresponding to workflow data
 * @param {string[]} newSubworkflows - list of locale specifiers for newly created subworkflows
 * @returns true if all subworkflows saved successfully; false otherwise
 */
export const saveSubworkflows = async (tenantId, workflowData, workflowGraphs, newSubworkflows = []) => {
  for (let idx = 0; idx < workflowData.subWorkflows.length; idx++) {
    const result = await saveSubworkflow(
      tenantId,
      workflowData.workflowId,
      workflowData.subWorkflows[idx],
      workflowGraphs[idx],
      newSubworkflows
    );
    if (!result) {
      // don't continue saving once one fails
      return false;
    }
  }
  return true;
};

export const saveSubworkflow = async (tenantId, workflowId, sub, workflowGraph, newSubworkflows) => {
  sub.tenantResources = workflowGraph.getResourcesList();
  sub.isTested = workflowGraph.isTested || sub.isTested;
  const specifier = createLocaleSpecifierFromParts(sub.sourceLocale, sub.targetLocale);
  let result = null;
  if (newSubworkflows.includes(specifier)) {
    // save it as a new subworkflow
    result = await addTenantSubworkflow(tenantId, workflowId, sub);
  } else {
    // update the existing subworkflow
    result = await updateTenantSubworkflow(tenantId, workflowId, sub);
  }
  return result;
};

export const buildSelectionAttributes = responseAttributes => {
  return new ImmutableMap(responseAttributes || []).mapEntries(([k, v]) => [nanoid(), { name: k, value: v }]);
};

export const calculateAllocationPercent = (allocation, numberOfBuckets) => {
  if (allocation.start === allocation.end) {
    return 0;
  }
  return Math.round(((allocation.end - allocation.start + 1) / numberOfBuckets) * 10000) / 100;
};
